import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';
import { useLab } from '../i18n/LabContext';

const fmt = n => Number(n || 0).toLocaleString();

const ROLE_KEYS = [
  { key: 'kassir',  tKey: 'roleKassir',  color: 'var(--cyan)' },
  // DIQQAT: `menejer` (uzbek) — AccessControl.js PRESET_ROLES bilan bir xil
  // nom. Ilgari `manager` (ingliz) edi — RBAC qidirishida staff.role va
  // role_permissions.role_name moslashmay xodim login qilsa hech qanday
  // sahifa ko'rinmasdi. Mavjud yozuvlar DB'da UPDATE qilingan.
  { key: 'menejer', tKey: 'roleManager', color: 'var(--purple)' },
  { key: 'texnik',  tKey: 'roleTechnic', color: 'var(--orange)' },
];

const roleColor = r => ROLE_KEYS.find(x => x.key === r)?.color || 'var(--text2)';

function Tab({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: '9px 22px', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontWeight: 600,
      background: active ? 'rgba(0,212,255,0.12)' : 'transparent',
      border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
      color: active ? 'var(--cyan)' : 'var(--text2)',
    }}>{label}</button>
  );
}

const Inp = ({ label, type, val, onChange, opts = {} }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{label}</label>
    <input type={type} value={val} onChange={e => onChange(e.target.value)} {...opts}
      style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
        color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13,
        outline: 'none', boxSizing: 'border-box' }} />
  </div>
);

const Sel = ({ label, val, onChange, options }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{label}</label>
    <select value={val} onChange={e => onChange(e.target.value)}
      style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
        color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13, outline: 'none' }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

function StaffTab() {
  const { t } = useLang();
  const { mode: labMode } = useLab();
  const isAllMode = labMode === 'all';
  const roleLabel = r => {
    const rk = ROLE_KEYS.find(x => x.key === r);
    return rk ? t(rk.tKey) : r;
  };

  const [staff, setStaff] = useState([]);
  const [labs, setLabs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', role: 'kassir', salary_type: 'monthly', rate: '', lab_id: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, l] = await Promise.all([api.get('/staff'), api.get('/owner/labs')]);
      setStaff(r.data);
      setLabs(l.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', phone: '', role: 'kassir', salary_type: 'monthly', rate: '', lab_id: labs[0]?.id || '', pin: '' });
    setShowForm(true);
  };

  const openEdit = s => {
    setEditing(s);
    setForm({ name: s.name, phone: s.phone || '', role: s.role, salary_type: s.salary_type || 'monthly', rate: s.rate || '', lab_id: s.lab_id || labs[0]?.id || '', pin: '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) return alert(t('staffNameRequired'));
    if (form.pin && form.pin.trim().length < 4) return alert(t('pinMin4') || 'PIN kamida 4 ta raqam');
    try {
      const payload = { ...form };
      if (!payload.pin || !payload.pin.trim()) delete payload.pin;
      editing ? await api.put(`/staff/${editing.id}`, payload) : await api.post('/staff', payload);
      setShowForm(false); load();
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const toggleActive = async s => {
    try { await api.put(`/staff/${s.id}`, { is_active: !s.is_active }); load(); }
    catch (e) { alert(t('error')); }
  };

  const handleDelete = async id => {
    if (!window.confirm(t('confirmDeleteStaff'))) return;
    try { await api.delete(`/staff/${id}`); load(); }
    catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const rateLabel = type => {
    if (type === 'hourly') return t('rateHourlyLabel');
    if (type === 'shift')  return t('rateShiftLabel');
    if (type === 'weekly') return t('rateWeeklyLabel');
    return t('rateMonthlyLabel');
  };

  const rateSuffix = type => {
    if (type === 'hourly') return t('ratePerHour');
    if (type === 'shift')  return t('ratePerShift');
    if (type === 'weekly') return t('ratePerWeek');
    return t('ratePerMonth');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        {isAllMode ? (
          <div style={{
            background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 10,
            padding: '10px 16px', fontSize: 12, color: 'var(--text2)',
          }}>🌐 Hammasi — xodim qo'shish/tahrirlash uchun klub tanlang</div>
        ) : <div />}
        {!showForm && !isAllMode && (
          <button onClick={openAdd} style={{
            background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)',
            color: 'var(--green)', padding: '10px 22px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700,
          }}>{t('addStaff')}</button>
        )}
      </div>

      {showForm && (
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 18px', fontSize: 15 }}>{editing ? t('editStaffTitle') : t('newStaffTitle')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Inp label={t('staffNameLabel')} type="text" val={form.name} onChange={v => set('name', v)} opts={{ placeholder: t('staffNamePlh') }} />
            <Inp label={t('staffPhoneLabel')} type="text" val={form.phone} onChange={v => set('phone', v)} opts={{ placeholder: '+998...' }} />
            <Sel label={t('positionLabel')} val={form.role} onChange={v => set('role', v)} options={ROLE_KEYS.map(r => ({ value: r.key, label: roleLabel(r.key) }))} />
            {false && labs.length > 1 ? (
              <Sel label={t('branchLabel')} val={String(form.lab_id)} onChange={v => set('lab_id', v)} options={labs.map(l => ({ value: String(l.id), label: l.name }))} />
            ) : null}
            <Sel label={t('salaryTypeLabel')} val={form.salary_type} onChange={v => set('salary_type', v)} options={[
              { value: 'monthly', label: t('salaryMonthly') },
              { value: 'weekly',  label: t('salaryWeekly') },
              { value: 'shift',   label: t('salaryShift') },
              { value: 'hourly',  label: t('salaryHourly') },
            ]} />
            <Inp
              label={rateLabel(form.salary_type)}
              type="number" val={form.rate} onChange={v => set('rate', v)} opts={{ placeholder: '0', min: '0' }} />
            <Inp
              label={`🔐 ${t('pinLabel') || 'PIN kod'}${editing ? ` (${t('pinLeaveBlank') || "bo'sh qoldirsangiz o'zgarmaydi"})` : ''}`}
              type="password"
              val={form.pin || ''}
              onChange={v => set('pin', v.replace(/\D/g, '').slice(0, 12))}
              opts={{ placeholder: editing ? '••••' : (t('pinPlh') || '4+ ta raqam'), inputMode: 'numeric', autoComplete: 'new-password', maxLength: 12 }} />
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <button onClick={() => setShowForm(false)} style={{ flex: 1, background: 'transparent',
              border: '1px solid var(--border)', color: 'var(--text2)', padding: '10px', borderRadius: 9, cursor: 'pointer', fontSize: 13 }}>
              {t('cancelShort')}
            </button>
            <button onClick={handleSave} style={{ flex: 2, background: 'rgba(0,255,136,0.12)',
              border: '1px solid var(--green)', color: 'var(--green)', padding: '10px', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
              💾 {t('save')}
            </button>
          </div>
        </div>
      )}

      <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{t('loading')}</div>
        ) : staff.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
            <div style={{ fontSize: 15, color: 'var(--text2)' }}>{t('noStaff')}</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--card)' }}>
                {[t('colStaffName'), t('colPhone'), t('colPosition'), t('colRate'), t('colActive'), t('colActions')].map(h => (
                  <th key={h} style={{ padding: '12px 20px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staff.map(s => (
                <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '12px 20px', fontWeight: 600 }}>{s.name}</td>
                  <td style={{ padding: '12px 20px', color: 'var(--text2)' }}>{s.phone || '—'}</td>
                  <td style={{ padding: '12px 20px' }}>
                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600,
                      background: roleColor(s.role) + '18', color: roleColor(s.role),
                      border: `1px solid ${roleColor(s.role)}40` }}>
                      {roleLabel(s.role)}
                    </span>
                  </td>
                  <td style={{ padding: '12px 20px' }}>
                    {fmt(s.rate)} {t('currency')}{rateSuffix(s.salary_type)}
                  </td>
                  <td style={{ padding: '12px 20px' }}>
                    <button onClick={() => toggleActive(s)} style={{
                      width: 42, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                      background: s.is_active ? 'var(--green)' : 'var(--border)', position: 'relative',
                    }}>
                      <span style={{
                        position: 'absolute', top: 3, left: s.is_active ? 22 : 3,
                        width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
                      }} />
                    </button>
                  </td>
                  <td style={{ padding: '12px 20px' }}>
                    {isAllMode ? (
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>🌐</span>
                    ) : (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(s)} style={{
                          background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)',
                          padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>✏️</button>
                        <button onClick={() => handleDelete(s.id)} style={{
                          background: 'transparent', border: '1px solid rgba(255,59,92,0.4)', color: 'var(--red)',
                          padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SalaryTab() {
  const { t } = useLang();
  const roleLabel = r => {
    const rk = ROLE_KEYS.find(x => x.key === r);
    return rk ? t(rk.tKey) : r;
  };

  const now = new Date();
  const firstDay = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`;
  const lastDay  = new Date(now.getFullYear(), now.getMonth()+1, 0);
  const lastDayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(lastDay.getDate()).padStart(2,'0')}`;

  const [staff, setStaff]         = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [month, setMonth]          = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
  const [dateFrom, setDateFrom]    = useState(firstDay);
  const [dateTo, setDateTo]        = useState(lastDayStr);
  const [history, setHistory]      = useState([]);
  const [summary, setSummary]      = useState(null);
  const [shiftStats, setShiftStats] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [form, setForm]            = useState({ base_units: '', base_amount: '', bonus: '', fine: '', advance: '', notes: '' });
  const [total, setTotal]          = useState(0);

  useEffect(() => {
    api.get('/staff').then(r => setStaff(r.data)).catch(() => {});
    api.get('/staff/summary').then(r => setSummary(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    api.get(`/staff/${selectedId}/salary`).then(r => setHistory(r.data)).catch(() => {});
    setShiftStats(null);
    setForm({ base_units: '', base_amount: '', bonus: '', fine: '', advance: '', notes: '' });
    setTotal(0);
  }, [selectedId]);

  const selectedStaff = staff.find(s => String(s.id) === String(selectedId));

  const calcTotal = (f) => {
    return Number(f.base_amount||0) + Number(f.bonus||0) - Number(f.fine||0) - Number(f.advance||0);
  };

  const set = (k, v) => {
    const updated = { ...form, [k]: v };
    const rate = Number(selectedStaff?.rate || 0);
    const type = selectedStaff?.salary_type;
    if (k === 'base_units' && type !== 'monthly') {
      updated.base_amount = String(Number(v||0) * rate);
    }
    setTotal(calcTotal(updated));
    setForm(updated);
  };

  const autoCalc = async () => {
    if (!selectedId) return;
    const type = selectedStaff?.salary_type;
    if (type !== 'monthly' && (!dateFrom || !dateTo)) return;
    setCalcLoading(true);
    try {
      const params = type === 'monthly' ? '' : `?from=${dateFrom}&to=${dateTo}`;
      const r = await api.get(`/staff/${selectedId}/auto-calc${params}`);
      setShiftStats(r.data);
      const newForm = { ...form };
      if (r.data.base_amount != null) newForm.base_amount = String(r.data.base_amount);
      if (type === 'shift')  newForm.base_units = String(r.data.shifts_count ?? '');
      if (type === 'hourly') newForm.base_units = String(r.data.total_hours ?? '');
      if (type === 'weekly') newForm.base_units = String(r.data.weeks_count ?? '');
      setTotal(calcTotal(newForm));
      setForm(newForm);
    } catch (e) { alert(e.response?.data?.error || t('error')); }
    finally { setCalcLoading(false); }
  };

  const handleCreate = async () => {
    if (!selectedId || !form.base_amount) return alert(t('salaryStaffRequired'));
    try {
      await api.post(`/staff/${selectedId}/salary`, {
        ...form, month, total,
        shifts_count: shiftStats?.shifts_count || null,
      });
      setForm({ base_units: '', base_amount: '', bonus: '', fine: '', advance: '', notes: '' });
      setTotal(0); setShiftStats(null);
      const r = await api.get(`/staff/${selectedId}/salary`);
      setHistory(r.data);
      alert(t('salarySaved'));
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const markPaid = async id => {
    try {
      await api.put(`/staff/salary/${id}/pay`);
      const r = await api.get(`/staff/${selectedId}/salary`);
      setHistory(r.data);
    } catch (e) { alert(t('error')); }
  };

  const baseUnitsLabel = type => {
    if (type === 'hourly') return t('hoursUnitsLabel');
    if (type === 'shift')  return t('shiftsUnitsLabel');
    return t('weeksUnitsLabel');
  };

  return (
    <div>
      {summary && Array.isArray(summary) && (() => {
        const paid    = summary.filter(s => s.salary_status === 'paid').reduce((a, s) => a + Number(s.salary_total || 0), 0);
        const pending = summary.filter(s => s.salary_status === 'pending').reduce((a, s) => a + Number(s.salary_total || 0), 0);
        const totalAmt = paid + pending;
        return (
          <div style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.25)',
            borderRadius: 14, padding: '16px 24px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 32 }}>💰</div>
            <div>
              <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 20, fontWeight: 700, color: 'var(--cyan)' }}>
                {fmt(totalAmt)} {t('currency')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{t('monthlyTotalSummary')} ({summary.filter(s=>s.salary_status!=='no_record').length} {t('staffCountSuffix')})</div>
            </div>
            {paid > 0 && (
              <div>
                <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--green)' }}>{fmt(paid)} {t('currency')}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>✅ {t('salaryPaid')}</div>
              </div>
            )}
            {pending > 0 && (
              <div>
                <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--orange)' }}>{fmt(pending)} {t('currency')}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>⏳ {t('salaryPending')}</div>
              </div>
            )}
          </div>
        );
      })()}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{t('selectStaffLabel')}</label>
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
            style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
              color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13, outline: 'none' }}>
            <option value="">{t('selectStaffPlh')}</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({roleLabel(s.role)})</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{t('monthLabel')}</label>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
              color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        </div>
      </div>

      {selectedId && (
        <>
          <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>{t('calcSalaryTitle')}</h3>
            {selectedStaff && (
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14 }}>
                {selectedStaff.salary_type === 'hourly'  && `🕐 ${t('salaryHourly')} · ${Number(selectedStaff.rate||0).toLocaleString()} ${t('currency')}${t('ratePerHour')}`}
                {selectedStaff.salary_type === 'shift'   && `⏱ ${t('salaryShift')} · ${Number(selectedStaff.rate||0).toLocaleString()} ${t('currency')}${t('ratePerShift')}`}
                {selectedStaff.salary_type === 'weekly'  && `📆 ${t('salaryWeekly')} · ${Number(selectedStaff.rate||0).toLocaleString()} ${t('currency')}${t('ratePerWeek')}`}
                {selectedStaff.salary_type === 'monthly' && `📅 ${t('salaryMonthly')} · ${Number(selectedStaff.rate||0).toLocaleString()} ${t('currency')}${t('ratePerMonth')}`}
              </div>
            )}

            {selectedStaff && (
              <div style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10, fontWeight: 600 }}>{t('autoCalcTitle')}</div>
                {selectedStaff.salary_type !== 'monthly' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{t('dateFromLabel')}</div>
                      <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                        style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{t('dateToLabel')}</div>
                      <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                        style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <button onClick={autoCalc} disabled={calcLoading}
                      style={{ background: 'var(--cyan)', border: 'none', color: '#000', padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {calcLoading ? '...' : t('calcBtn')}
                    </button>
                  </div>
                )}
                {selectedStaff.salary_type === 'monthly' && (
                  <button onClick={autoCalc} disabled={calcLoading}
                    style={{ background: 'var(--cyan)', border: 'none', color: '#000', padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
                    {calcLoading ? '...' : t('fillMonthlyBtn')}
                  </button>
                )}
                {shiftStats && (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--cyan)', marginBottom: 8, fontStyle: 'italic' }}>{shiftStats.info}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                      {shiftStats.salary_type === 'shift' && <>
                        <div style={{ background: 'var(--card)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--cyan)' }}>{shiftStats.shifts_count}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('shiftsCountLabel')}</div>
                        </div>
                        <div style={{ background: 'var(--card)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--orange)' }}>{Number(selectedStaff.rate).toLocaleString()}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('shiftPriceLabel')}</div>
                        </div>
                      </>}
                      {shiftStats.salary_type === 'hourly' && <>
                        <div style={{ background: 'var(--card)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--cyan)' }}>{shiftStats.total_hours}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('hoursCountLabel')}</div>
                        </div>
                        <div style={{ background: 'var(--card)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--orange)' }}>{Number(selectedStaff.rate).toLocaleString()}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('hourPriceLabel')}</div>
                        </div>
                      </>}
                      {shiftStats.salary_type === 'weekly' && <>
                        <div style={{ background: 'var(--card)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--cyan)' }}>{shiftStats.weeks_count}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('weeksCountLabel')}</div>
                        </div>
                        <div style={{ background: 'var(--card)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--orange)' }}>{Number(selectedStaff.rate).toLocaleString()}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('weekPriceLabel')}</div>
                        </div>
                      </>}
                      <div style={{ background: 'var(--card)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--green)' }}>{Number(shiftStats.base_amount).toLocaleString()}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('calcSalaryResult')}</div>
                      </div>
                    </div>
                    {shiftStats.recent_sessions?.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>{t('recentSessionsLabel')}</div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <thead>
                            <tr style={{ background: 'var(--card)' }}>
                              {[t('colStarted'), t('colEnded'), t('colDurationHdr')].map(h => (
                                <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {shiftStats.recent_sessions.map((s, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ padding: '5px 10px', color: 'var(--text)' }}>{new Date(s.started_at).toLocaleString('uz-UZ', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}</td>
                                <td style={{ padding: '5px 10px', color: 'var(--text)' }}>{s.ended_at ? new Date(s.ended_at).toLocaleString('uz-UZ', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}</td>
                                <td style={{ padding: '5px 10px', color: 'var(--cyan)' }}>{s.duration_minutes ? `${Math.floor(s.duration_minutes/60)}s ${s.duration_minutes%60}d` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              {selectedStaff?.salary_type !== 'monthly' && (
                <Inp
                  label={baseUnitsLabel(selectedStaff?.salary_type)}
                  type="number" val={form.base_units} onChange={v => set('base_units', v)} opts={{ placeholder: '0', min: '0' }} />
              )}
              <Inp label={t('baseAmountLabel')} type="number" val={form.base_amount} onChange={v => set('base_amount', v)} opts={{ placeholder: '0', min: '0' }} />
              <Inp label={t('bonusLabel')} type="number" val={form.bonus} onChange={v => set('bonus', v)} opts={{ placeholder: '0', min: '0' }} />
              <Inp label={t('fineLabel')} type="number" val={form.fine} onChange={v => set('fine', v)} opts={{ placeholder: '0', min: '0' }} />
              <Inp label={t('advanceLabel')} type="number" val={form.advance} onChange={v => set('advance', v)} opts={{ placeholder: '0', min: '0' }} />
              <Inp label={t('notesLabel')} type="text" val={form.notes} onChange={v => set('notes', v)} opts={{ placeholder: t('optionalPlh') }} />
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 6 }}>
              <div style={{ flex: 1, background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 9, padding: '10px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>{t('totalSalaryLabel')}</span>
                <span style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 16, fontWeight: 700,
                  color: total >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(total)} {t('currency')}</span>
              </div>
              <button onClick={handleCreate} style={{
                background: 'rgba(0,255,136,0.12)', border: '1px solid var(--green)',
                color: 'var(--green)', padding: '10px 22px', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                💾 {t('save')}
              </button>
            </div>
          </div>

          <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 11,
              color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>{t('salaryHistoryTitle')}</div>
            {history.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}>{t('noData')}</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--card)' }}>
                    {[t('colMonth'), t('colBase'), t('colBonus'), t('colFine'), t('colAdvance'), t('colTotal'), t('colStatus'), ''].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 16px', fontWeight: 600 }}>{r.month}</td>
                      <td style={{ padding: '10px 16px' }}>{fmt(r.base_amount)} {t('currency')}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--green)' }}>{fmt(r.bonus)} {t('currency')}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--red)' }}>{fmt(r.fine)} {t('currency')}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--orange)' }}>{fmt(r.advance)} {t('currency')}</td>
                      <td style={{ padding: '10px 16px', fontWeight: 700, fontFamily: 'Orbitron, sans-serif', fontSize: 12 }}>{fmt(r.total)} {t('currency')}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600,
                          background: r.is_paid ? 'rgba(0,255,136,0.12)' : 'rgba(255,159,67,0.12)',
                          color: r.is_paid ? 'var(--green)' : 'var(--orange)',
                          border: `1px solid ${r.is_paid ? 'rgba(0,255,136,0.3)' : 'rgba(255,159,67,0.3)'}` }}>
                          {r.is_paid ? t('salaryMarkedPaid') : t('salaryPendingStatus')}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        {!r.is_paid && (
                          <button onClick={() => markPaid(r.id)} style={{
                            background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)',
                            color: 'var(--green)', padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 11 }}>
                            {t('markPaidBtn')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DavomatTab() {
  const { t } = useLang();
  const roleLabel = r => {
    const rk = ROLE_KEYS.find(x => x.key === r);
    return rk ? t(rk.tKey) : r;
  };

  const [staff, setStaff]             = useState([]);
  const [activeSessions, setActive]   = useState([]);
  const [selectedId, setSelectedId]   = useState('');
  const [workHistory, setHistory]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [, setTick]                   = useState(0);

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([api.get('/staff'), api.get('/staff/work/active')]);
      setStaff(r.data);
      setActive(a.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const timer = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedId) { setHistory([]); return; }
    api.get(`/staff/${selectedId}/work`).then(r => setHistory(r.data)).catch(() => {});
  }, [selectedId]);

  const getSession = id => activeSessions.find(s => s.staff_id === id);

  const getDuration = startedAt => {
    const mins = Math.floor((Date.now() - new Date(startedAt)) / 60000);
    if (mins < 60) return `${mins} daq`;
    return `${Math.floor(mins / 60)}s ${mins % 60}d`;
  };

  const fmtDur = mins => {
    if (!mins) return '—';
    const h = Math.floor(mins / 60), m = mins % 60;
    return h > 0 ? `${h}s ${m}d` : `${m}d`;
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 28 }}>
        {loading ? (
          <div style={{ gridColumn: '1/-1', padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{t('loading')}</div>
        ) : staff.length === 0 ? (
          <div style={{ gridColumn: '1/-1', padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{t('noStaff')}</div>
        ) : staff.map(s => {
          const session = getSession(s.id);
          const active  = !!session;
          return (
            <div key={s.id} style={{
              background: active ? 'rgba(0,255,136,0.05)' : 'var(--card2)',
              border: `1px solid ${active ? 'rgba(0,255,136,0.35)' : 'var(--border)'}`,
              borderRadius: 14, padding: '16px 18px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: roleColor(s.role), marginTop: 2 }}>{roleLabel(s.role)}</div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                  color: active ? 'var(--green)' : 'var(--text3)',
                  background: active ? 'rgba(0,255,136,0.12)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${active ? 'rgba(0,255,136,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
                  {active ? t('activeAtWork') : t('restingLabel')}
                </div>
              </div>
              {active && session && (
                <div style={{ fontSize: 11, color: 'var(--cyan)', marginBottom: 8 }}>⏱ {getDuration(session.started_at)}</div>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setSelectedId(String(s.id) === selectedId ? '' : String(s.id))} style={{
                  flex: 1,
                  background: selectedId === String(s.id) ? 'rgba(0,212,255,0.1)' : 'transparent',
                  border: `1px solid ${selectedId === String(s.id) ? 'var(--cyan)' : 'var(--border)'}`,
                  color: selectedId === String(s.id) ? 'var(--cyan)' : 'var(--text2)',
                  padding: '7px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                }}>{t('workHistoryBtn')}</button>
              </div>
            </div>
          );
        })}
      </div>

      {selectedId && (
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
              {staff.find(s => String(s.id) === selectedId)?.name} — {t('workHistoryLabel')}
            </span>
            <button onClick={() => setSelectedId('')} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16 }}>✕</button>
          </div>
          {workHistory.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}>{t('noWorkHistory')}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--card)' }}>
                  {[t('colDate'), t('colStartWork'), t('colEndWork'), t('colDurationHdr'), t('colStatus')].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {workHistory.map((w, i) => {
                  const start = w.started_at ? new Date(w.started_at) : null;
                  const end   = w.ended_at   ? new Date(w.ended_at)   : null;
                  const sana  = start ? start.toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'short' }) : '—';
                  const bosh  = start ? start.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' }) : '—';
                  const yakun = end   ? end.toLocaleTimeString('uz-UZ',   { hour: '2-digit', minute: '2-digit' }) : '—';
                  return (
                    <tr key={`${w.source}-${w.id}-${i}`} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 16px', fontWeight: 600 }}>{sana}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--green)' }}>{bosh}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--red)' }}>{yakun}</td>
                      <td style={{ padding: '10px 16px', fontWeight: 700, color: 'var(--cyan)' }}>{fmtDur(w.duration_minutes)}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, fontWeight: 600,
                          background: w.status === 'active' ? 'rgba(0,255,136,0.12)' : 'rgba(255,255,255,0.05)',
                          color: w.status === 'active' ? 'var(--green)' : 'var(--text3)',
                          border: `1px solid ${w.status === 'active' ? 'rgba(0,255,136,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
                          {w.status === 'active' ? t('activeAtWork') : t('finishedStatus')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function Staff() {
  const { t } = useLang();
  const [tab, setTab] = useState('staff');
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>👥 {t('staffPageTitle')}</h1>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>{t('staffPageSubtitle')}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
        <Tab active={tab === 'staff'}   onClick={() => setTab('staff')}   label={t('tabStaff')} />
        <Tab active={tab === 'salary'}  onClick={() => setTab('salary')}  label={t('tabSalary')} />
        <Tab active={tab === 'davomat'} onClick={() => setTab('davomat')} label={t('tabAttendance')} />
      </div>
      {tab === 'staff'   && <StaffTab />}
      {tab === 'salary'  && <SalaryTab />}
      {tab === 'davomat' && <DavomatTab />}
    </div>
  );
}
